import { PRODUCTS } from '@app/shared/brand'
import {
  BookOpen,
  FileText,
  Image,
  Music,
  Presentation,
  Type,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { SiteShell } from '../../ui/SiteShell'
import { WayIn } from '../../ui/WayIn'
import { Point, Section } from './Section'
import { Tile } from './Tile'
import './marketing.css'

/**
 * Studio's front door, for somebody arriving signed out.
 *
 * ## Shape: every kind of work, then each one in turn
 *
 * The claim is the design of all media, so the page states the breadth first —
 * one equal tile per kind of work, nothing outranking anything — and each tile
 * leads to the section that says what that kind does. A page that opens on the
 * book alone reads as a typesetting program, which is a smaller product than
 * this is.
 *
 * What a person keeps here is their own, so there is nothing public to sample
 * the way a shul's schedule is public. The page says what can be made instead,
 * and every sentence on it is something the product does today — a claim with
 * nothing behind it is the failure a front door like this slides into.
 *
 * What the kinds share closes the page: one source for screen, paper and a link,
 * and the people a work is shared with reading it together.
 *
 * A point is a heading and one sentence, as on every front page here.
 */
export function StudioHomePage({ onLogin }: { onLogin?: () => void }) {
  return (
    <SiteShell onLogin={onLogin}>
      <Hero onLogin={onLogin} />
      <Kinds />
      <Books />
      <PagesAndDecks />
      <Songs />
      <Together />
    </SiteShell>
  )
}

function Hero({ onLogin }: { onLogin?: () => void }) {
  return (
    <section className="mk-hero">
      <div className="mk-measure mk-hero__inner">
        <p className="mk-label">{PRODUCTS.studio.name}</p>
        <h1 className="mk-hero__title">
          Books, pages, decks and songs, designed in one place.
        </h1>
        <p className="mk-hero__lede">
          Everything you make is held as its content, not as a file — so a
          correction made once is right on screen, on paper and in every link
          you have shared.
        </p>
        <div className="mk-hero__actions">
          <WayIn onSignIn={onLogin} />
          <a className="mk-textlink" href="#make">
            What you can make
          </a>
        </div>
      </div>
    </section>
  )
}

type Kind = {
  href: string
  icon: ComponentType<{ className?: string }>
  name: string
  line: string
}

const KINDS: Kind[] = [
  {
    href: '#books',
    icon: BookOpen,
    name: 'Books',
    line: 'A sefer with its sources, notes and citations, printed with a real contents page.',
  },
  {
    href: '#pages',
    icon: FileText,
    name: 'Pages',
    line: 'A flyer, a notice or a sign, on any paper, filled in from live data.',
  },
  {
    href: '#pages',
    icon: Presentation,
    name: 'Decks',
    line: 'Slides you present from, with what you mean to say beside them.',
  },
  {
    href: '#songs',
    icon: Music,
    name: 'Songs',
    line: 'Chords over words, in any key, with the strum and the melody.',
  },
  {
    href: '#pages',
    icon: Image,
    name: 'Covers and media',
    line: 'A cover for every work, and the pictures it uses, kept with it.',
  },
  {
    href: '#books',
    icon: Type,
    name: 'Hebrew and English',
    line: 'Both scripts set together, in faces drawn to sit side by side.',
  },
]

/**
 * Every kind of work, as equal tiles. Each is a link down the page rather than a
 * panel, because the section it leads to is where that kind's case is made.
 */
function Kinds() {
  return (
    <Section id="make" label="What you make" title="One place for all of it.">
      <ul className="mk-tiles">
        {KINDS.map((k) => (
          <li key={k.name}>
            <Tile href={k.href} icon={k.icon} name={k.name} line={k.line} />
          </li>
        ))}
      </ul>
    </Section>
  )
}

function Books() {
  return (
    <Section
      id="books"
      label="Books"
      title="Hold a sefer as data. Print it as a book."
    >
      <div className="mk-pair">
        <Point title="Sources beside the text">
          Bring in a work and quote it — the citation stays tied to the place it
          came from.
        </Point>
        <Point title="Every version kept">
          Name the moments that matter and go back to any of them.
        </Point>
        <Point title="Pages a printer accepts">
          Running heads, a contents page with real page numbers, and notes at
          the foot of the page they belong to.
        </Point>
        <Point title="Hebrew and English together">
          A bilingual work is set in faces drawn as one family, so the two
          scripts sit on the page as a pair.
        </Point>
      </div>
    </Section>
  )
}

function PagesAndDecks() {
  return (
    <Section
      id="pages"
      label="Pages, decks and covers"
      title="A sheet of paper, or a room to present to."
    >
      <div className="mk-pair mk-pair--three">
        <Point title="Pages that fill themselves in">
          Lay it out once on the paper you print on; the live figures it names
          are read fresh each time it is shown or printed.
        </Point>
        <Point title="Decks you present from">
          Present full screen with your notes kept under each slide, and
          download the slides or a notes copy as a PDF.
        </Point>
        <Point title="A cover for every work">
          Each book, page and deck carries a cover of its own, and the pictures
          it uses are kept with it.
        </Point>
      </div>
    </Section>
  )
}

function Songs() {
  return (
    <Section
      id="songs"
      label="Songs"
      title="Chords over the words they go with."
    >
      <div className="mk-pair mk-pair--three">
        <Point title="Any key, any capo">
          Move a song to the key you sing it in, and the chords follow.
        </Point>
        <Point title="The strum and the tune">
          Keep the pattern and the melody with the chords, not on a separate
          sheet.
        </Point>
        <Point title="A sheet for the band">
          Put it on a screen or download it as ChordPro — the same song either
          way.
        </Point>
      </div>
    </Section>
  )
}

function Together() {
  return (
    <Section
      id="together"
      label="Together"
      title="Share one work, and read it together."
    >
      <div className="mk-pair mk-pair--three">
        <Point title="Share a work, not your library">
          Everything is private to you until you share one thing, with one
          person or with a link.
        </Point>
        <Point title="Comments on the passage">
          A remark sits on the words it is about, with its replies, until
          somebody resolves it.
        </Point>
        <Point title="Read it in step">
          See who else is reading, follow where they are, or lead them through
          it.
        </Point>
      </div>
    </Section>
  )
}
